import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material';
import { useDatabase } from '../../hooks/useDatabase';
import { Event } from '../../types/database';

interface QuantUpdate {
  id: string;
  title: string;
  body: string;
  timestamp: string;
  category: 'market' | 'portfolio' | 'research' | 'news';
}


// Helper function to determine category from event metadata or title
const determineCategory = (event: Event): QuantUpdate['category'] => {
  const title = event.title.toLowerCase();
  const description = event.description?.toLowerCase() || '';
  const text = `${title} ${description}`;
  
  if (text.includes('market') || text.includes('volatility') || text.includes('price') || text.includes('trading')) {
    return 'market';
  } else if (text.includes('portfolio') || text.includes('investment') || text.includes('asset') || text.includes('allocation')) {
    return 'portfolio';
  } else if (text.includes('research') || text.includes('analysis') || text.includes('correlation') || text.includes('pattern')) {
    return 'research';
  } else if (text.includes('news') || text.includes('fed') || text.includes('economic') || text.includes('announcement')) {
    return 'news';
  }
  
  // Default to market if no clear category
  return 'market';
};

interface QuantUpdatesProps {
  limit?: number;
  onLoadMore?: () => void;
  events?: Event[];
}

export function QuantUpdates({ limit = 5, onLoadMore, events: eventsProp }: QuantUpdatesProps) {
  const navigate = useNavigate();
  const db = useDatabase();
  const [displayLimit, setDisplayLimit] = useState(limit);
  const [loading, setLoading] = useState(false);

  // Use events from prop if provided, otherwise fetch them
  const [localEvents, setLocalEvents] = useState<Event[]>([]);
  const events = eventsProp || localEvents;

  const loadQuantUpdates = useCallback(async () => {
    if (eventsProp) {
      // If events are provided as prop, don't fetch
      return;
    }

    try {
      setLoading(true);
      const { data: eventsData, error } = await db.getEvents();
      
      if (error) {
        console.error('Error fetching events:', error);
        return;
      }

      setLocalEvents(eventsData || []);
    } catch (error) {
      console.error('Error loading quant updates:', error);
    } finally {
      setLoading(false);
    }
  }, [db, eventsProp]);

  useEffect(() => {
    loadQuantUpdates();
  }, [loadQuantUpdates]);

  // Filter for unread quant_update events and transform them
  const quantUpdates = useMemo(() => {
    return events
      ?.filter(event => 
        event.metadata?.type === 'quant_update' && 
        !event.metadata?.is_read
      )
      ?.map(event => ({
        id: event.id,
        title: event.title,
        body: event.description || '',
        timestamp: event.created_at,
        category: determineCategory(event)
      }))
      ?.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()) || [];
  }, [events]);

  const handleLoadMore = () => {
    setDisplayLimit(prev => prev + 5);
    onLoadMore?.();
  };

  const handleMarkAsRead = async (updateId: string, action: 'thumbs_up' | 'ignore') => {
    try {
      const { error } = await db.markEventAsRead(updateId, action);
      if (error) {
        console.error('Error marking event as read:', error);
        return;
      }
      // If events are provided as prop, parent will handle refresh
      // Otherwise reload locally
      if (!eventsProp) {
        await loadQuantUpdates();
      }
    } catch (error) {
      console.error('Error marking event as read:', error);
    }
  };

  const displayedUpdates = quantUpdates.slice(0, displayLimit);

  // Show loading only if we're fetching events ourselves
  const isLoading = loading && !eventsProp;

  if (isLoading) {
    return (
      <Box sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        p: 1.5,
        gridColumn: { xs: '1', sm: 'span 1' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200
      }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      p: 1.5,
      gridColumn: { xs: '1', sm: 'span 1' }
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
          Quant Updates
        </Typography>
      </Box>
      
      <List sx={{ p: 0 }}>
        {displayedUpdates.length === 0 ? (
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText 
              primary={
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No unread quant updates. 👍
                </Typography>
              }
            />
          </ListItem>
        ) : (
          displayedUpdates.map((update, idx) => (
            <React.Fragment key={update.id}>
              <ListItem
                sx={{ 
                  py: 1.5, 
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 0.5,
                  '&:hover': { 
                    backgroundColor: 'action.hover'
                  } 
                }}
              >
                {/* Title and Buttons on Same Line */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.5
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.primary', 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      lineHeight: 1.2,
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    {update.title}
                  </Typography>
                  
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(update.id, 'thumbs_up');
                      }}
                      sx={{ 
                        minWidth: { xs: 44, sm: 48 },
                        height: { xs: 36, sm: 40 },
                        borderRadius: 1,
                        borderColor: 'text.secondary',
                        color: 'text.secondary',
                        fontSize: { xs: '1.2rem', sm: '1.4rem' },
                        fontWeight: 'bold',
                        px: { xs: 1, sm: 1.5 },
                        '&:hover': {
                          backgroundColor: 'text.secondary',
                          color: 'white'
                        }
                      }}
                    >
                      👍
                    </Button>
                    
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(update.id, 'ignore');
                      }}
                      sx={{ 
                        minWidth: { xs: 60, sm: 70 },
                        height: { xs: 36, sm: 40 },
                        borderRadius: 1,
                        borderColor: 'text.secondary',
                        color: 'text.secondary',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 'bold',
                        px: { xs: 1, sm: 1.5 },
                        '&:hover': {
                          backgroundColor: 'text.secondary',
                          color: 'white'
                        }
                      }}
                    >
                      Not Interested
                    </Button>
                  </Box>
                </Box>
                
                {/* Description - Full Width Below */}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: { xs: 3, sm: 2 },
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {update.body}
                </Typography>
              </ListItem>
              {idx < displayedUpdates.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
        
        {/* View All button after 3rd update */}
        {displayedUpdates.length >= 3 && (
          <>
            <Divider />
            <ListItem sx={{ py: 1 }}>
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/activity?type=event&event_subtype=quant_update')}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  View All Quant Updates
                </Button>
              </Box>
            </ListItem>
          </>
        )}

        {displayLimit < quantUpdates.length && (
          <>
            <Divider />
            <ListItem
              onClick={handleLoadMore}
              sx={{ 
                py: 0.5, 
                cursor: 'pointer', 
                '&:hover': { 
                  backgroundColor: 'action.hover'
                } 
              }}
            >
              <ListItemText
                primary={
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'primary.main',
                      fontStyle: 'italic',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Load More
                  </Typography>
                }
              />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );
}
